import { apiFetch } from '@nrdy-marketing-engine/api-fetch';
import { PriceItem } from '@nrdy-marketing-engine/cart/@types/price-item';
import { CatalogItem } from '../../../@types/catalog-item';
import { CURRENCY_CODE_MAP } from '../../constants/currency-code-map';
import { jurisdictionToCountryCode } from '../../lib/country-code/jurisdiction-to-country-code';
import fetchPrices from './fetch-prices';

export const FORMATTED_DESCRIPTION_PRICE =
  /^price:\s[\$\£\€][0-9]+(?:\.[0-9]{2})?\s?[a-zA-Z0-9_-]*/i;

function catalogItemHasDescriptionFormatted(item?: CatalogItem): boolean {
  return (
    item?.description_formatted && !!item?.description_formatted.match(FORMATTED_DESCRIPTION_PRICE)
  );
}

export function localizeItemPriceToClient(
  item: CatalogItem,
  prices: PriceItem[],
  jurisdictionId: number
): CatalogItem {
  const countryCode = jurisdictionToCountryCode(jurisdictionId);
  const currencyCode = CURRENCY_CODE_MAP[countryCode] || 'USD';

  const currencyAmount = prices?.find(
    (price) => price.currency_code === currencyCode && price.active
  )?.currency_amount;

  if (currencyCode === 'USD' || !currencyAmount) return item;

  const normalizedItem = {
    ...item,
    price_cents: currencyAmount,
  };

  return normalizedItem;
}

// this parses out the price from the description_formatted field
// and normalizes the item with the new price point.
export function normalizeItemWithDescriptionFormatted(item: CatalogItem): CatalogItem {
  const matches = item.description_formatted.match(FORMATTED_DESCRIPTION_PRICE) || [];
  const [descriptionPricePoint] = matches;
  const [_, priceValue, priceDuration] = descriptionPricePoint?.split(' ') || Array(3);

  let formattedDescriptionPriceProps = {
    subscription: item.subscription,
    price_in_description_formatted: false,
  };

  if (!!priceDuration) {
    const normalizedPriceDuration = priceDuration.split('_').join(' ');

    formattedDescriptionPriceProps = {
      subscription: { ...item.subscription, duration: normalizedPriceDuration },
      price_in_description_formatted: true,
    };
  }

  // use the price value to replace price_cents in the catalog item
  const price = parseInt(priceValue.replace(/\D+/g, ''));
  const santitizedDescription = item.description_formatted
    .replace(FORMATTED_DESCRIPTION_PRICE, '')
    .trim();

  return {
    ...item,
    price_cents: price,
    description_formatted: santitizedDescription,
    ...formattedDescriptionPriceProps,
  };
}

const fetchItemPricesAndLocalizeToClient = (item: CatalogItem, jurisdictionId: number) => {
  return fetchPrices(item.id).then((prices) =>
    localizeItemPriceToClient(item, prices, jurisdictionId)
  );
};

/**
 * this function is used to fetch the item from the catalog and normalize the price value
 * if the item has a price in the description_formatted field, it will take precedence over the localization of price.
 * if the item does not have a price in the description_formatted field, it will fetch the prices and localize the price to
 * the appropriate jurisdiction.
 *
 * Marketing is aware that this would cause issues if they attempted to add a description formatted value for an item that is
 * in a jurisdiction outside of the US and/or includes promo codes.

 * @param {string} itemId - item id to fetch
 * @param {number} jurisdictionId - jurisdiction id to localize the price to
 *
 * @return {CatalogItem} - the catalog item with the price normalized
 *
 * @ticket
 *
 *     ticket for reference https://varsity.atlassian.net/browse/MEX-12150
 */
const fetchItem = (itemId: string, jurisidictionId: number) =>
  apiFetch<CatalogItem>(`/v1/items/${itemId}`).then((item: CatalogItem) =>
    catalogItemHasDescriptionFormatted(item)
      ? normalizeItemWithDescriptionFormatted(item)
      : fetchItemPricesAndLocalizeToClient(item, jurisidictionId)
  );

export default fetchItem;
